import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@calendly/ui/components/dropdown-menu'
import { IconDevices } from '@calendly/ui/components/glyphs/IconDevices'
import { IconMoon } from '@calendly/ui/components/glyphs/IconMoon'
import { IconMoon2 } from '@calendly/ui/components/glyphs/IconMoon2'
import { IconSun } from '@calendly/ui/components/glyphs/IconSun'
import { IconButton } from '@calendly/ui/components/icon-button'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { useState } from 'react'
import type { Selection } from 'react-aria-components'

import { type ThemePreference, useThemePreference } from '../shell_layout/themePreferenceContext'

type ThemeOption = {
  key: ThemePreference
  label: string
  icon: Parameters<typeof UiIcon>[0]['icon']
}

const THEME_OPTIONS: ThemeOption[] = [
  { key: 'light', label: 'Light', icon: IconSun },
  { key: 'dark', label: 'Dark', icon: IconMoon },
  { key: 'system', label: 'System', icon: IconDevices },
]

/**
 * Theme selector icon button in the app bar that opens a dropdown menu with Light, Dark, and System options.
 */
export const ThemeSelectorMenu = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { themePreference, setThemePreference } = useThemePreference()

  const handleSelectionChange = (selection: Selection) => {
    const key = selection === 'all' ? undefined : [...selection][0]
    if (key === 'light' || key === 'dark' || key === 'system') {
      setThemePreference(key)
    }
    setIsOpen(false)
  }

  return (
    <DropdownMenu isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <DropdownMenuTrigger>
        <IconButton
          title="Select theme"
          appearance="secondary-outline"
          size="small"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <UiIcon icon={IconMoon2} size="small" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        placement="bottom end"
        selectionMode="single"
        selectedKeys={new Set([themePreference])}
        onSelectionChange={handleSelectionChange}
      >
        {THEME_OPTIONS.map(({ key, label, icon }) => (
          <DropdownMenuItem key={key} id={key}>
            <UiIcon icon={icon} size="small" />
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
